import { useQuery } from '@tanstack/react-query';
import { CircleAlert, CopyPlus } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge, type Tone } from '../../components/StatusBadge';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { useAuth } from '../auth/AuthProvider';
import { dayOf } from '../playback/time';
import { copiesKey, listCopies, type CopyStatus } from './api';
import { formatRange } from './format';

const statusView: Record<CopyStatus, { label: string; tone: Tone; pulse?: boolean }> = {
  queued: { label: 'Na fila', tone: 'neutral' },
  copying: { label: 'Copiando', tone: 'warn', pulse: true },
  finishing: { label: 'Finalizando', tone: 'warn', pulse: true },
  done: { label: 'Concluída', tone: 'ok' },
  failed: { label: 'Falhou', tone: 'crit' },
};

const failureText: Record<string, string> = {
  no_replay: 'O equipamento não abriu o vídeo do horário (fora do ar ou sem suporte).',
  no_video: 'Nenhum vídeo chegou do equipamento. Ele pode não ter gravação no trecho.',
  error: 'Não foi possível proteger a cópia.',
};

/** Copies of DVR/NVR recordings into the platform (ADR-042), newest first. */
export function CopiesPanel() {
  const { request, session } = useAuth();
  const tz = session?.tenant.timezone;
  const fmt = useTimeFormat();
  const copies = useQuery({
    queryKey: copiesKey,
    queryFn: () => listCopies(request),
    // Follow copies while they run (they take as long as the range).
    refetchInterval: (q) =>
      q.state.data?.items.some((c) => c.status !== 'done' && c.status !== 'failed') ? 5000 : false,
  });

  if (copies.isError && !copies.data) {
    return (
      <div className="rounded-lg border border-line bg-surface">
        <EmptyState
          tone="crit"
          icon={CircleAlert}
          title="Não foi possível carregar as cópias"
          description="Verifique a conexão com o servidor e tente novamente."
          action={
            <Button variant="primary" onClick={() => void copies.refetch()}>
              Tentar novamente
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      {copies.data?.items.length === 0 ? (
        <EmptyState
          icon={CopyPlus}
          title="Nenhuma cópia do equipamento"
          description="Na Reprodução, com a origem Equipamento, use “Copiar para o servidor” para guardar no Pro Guard um trecho gravado no DVR/NVR."
        />
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-[820px] text-left text-[13px]"
            aria-busy={copies.isFetching}
          >
            <caption className="sr-only">Cópias de gravações dos equipamentos</caption>
            <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
              <tr className="border-b border-line">
                <th scope="col" className="px-4 py-2 font-medium">
                  Equipamento e intervalo
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Motivo
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Solicitante
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Trechos
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Situação
                </th>
                <th scope="col" className="w-px px-4 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!copies.data
                ? Array.from({ length: 4 }, (_, i) => (
                    <tr key={i} aria-hidden="true" className="border-b border-line last:border-0">
                      {['w-48', 'w-40', 'w-24', 'ml-auto w-10', 'w-16', 'w-10'].map((w) => (
                        <td key={w} className="px-4 py-3.5">
                          <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                        </td>
                      ))}
                    </tr>
                  ))
                : copies.data.items.map((c) => {
                    const view = statusView[c.status];
                    return (
                      <tr
                        key={c.id}
                        className="border-b border-line align-top last:border-0 hover:bg-surface-hover/50"
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-fg">
                            {c.device.name || (
                              <span className="text-fg-subtle">Equipamento excluído</span>
                            )}
                          </div>
                          <div className="font-mono text-xs text-fg-subtle">
                            {formatRange(c.from, c.to, tz)}
                          </div>
                        </td>
                        <td className="max-w-xs px-4 py-2.5 text-fg-muted">
                          <p className="line-clamp-2 break-words">{c.reason}</p>
                        </td>
                        <td className="px-4 py-2.5 text-fg-muted">
                          <div>{c.created_by.name}</div>
                          <div className="text-xs text-fg-subtle">{fmt.absolute(c.created_at)}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                          {c.segments}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge tone={view.tone} pulse={view.pulse ?? false}>
                            {view.label}
                          </StatusBadge>
                          {c.status === 'failed' && (
                            <p className="mt-1 max-w-[16rem] text-xs text-fg-subtle">
                              {failureText[c.error_code ?? ''] ?? failureText.error}
                            </p>
                          )}
                          {c.status === 'done' && (
                            <p className="mt-1 text-xs whitespace-nowrap text-fg-subtle">
                              protegida por{' '}
                              {c.valid_days === 365 ? '1 ano' : `${String(c.valid_days)} dias`}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {c.status === 'done' && c.device.name && (
                            <Link
                              to={`/playback?device=${encodeURIComponent(c.device.id)}&day=${dayOf(Date.parse(c.from), tz)}`}
                              className="inline-flex h-7 items-center rounded-md px-2 text-[13px] text-accent hover:bg-surface-hover"
                            >
                              Reproduzir
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
